/**
 * Dashboard "Board" view: the pipeline as a kanban, one column per stage —
 * Scripting → Storyboarding → Request Review → Shooting → Editing.
 *
 * It used to fork (F-128): storyboarding and BGM ran as two parallel lanes
 * that rejoined at Shooting, because scoring gated the shoot. It does not any
 * more (F-133) — music is optional work that happens DURING the shoot — so the
 * lanes are gone and the kanban is a single row again. What music state a
 * board has is a TAG on the card from Shooting onwards: still scoring, or
 * every section covered.
 *
 * Dragging a card — or the card's "⋯" menu, which is also the keyboard/touch
 * path — starts a move, and the transition happens in a dialog because it
 * needs an owner for the new stage (AssignStatusDialog).
 *
 * The menu also carries the STORYBOARD SIGN-OFF for whoever holds
 * `review.approve`: approving from here is the point of the stage view — a
 * project manager works the column of boards waiting on them without opening
 * each one. Approval is deliberately not a drag: it is a decision with a note,
 * and the server (not the dragger) decides where the board lands.
 *
 * Archive is on the menu too — the only place it lives since the List view
 * went (2026-10). Archived boards never reach this kanban: with "Archived"
 * on, the dashboard lists them instead, each with Restore.
 */
import {
  BOARD_STATUSES,
  BOARD_STATUS_LABELS,
  showsBgmStems,
  type BoardStatus,
  type BoardSummary,
} from "@kola/shared";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { DateButton } from "../../ui/DateButton.js";
import {
  formatDateKey,
  formatDateKeyWithWeekday,
  isPastDateKey,
  todayDateKey,
} from "../../ui/date-key.js";
import { useKolaPortalTarget } from "../../ui/KolaRoot.js";
import { BgmStemsTag } from "../bgm/BgmStemsTag.js";
import { placeCardMenu, type CardMenuPosition } from "./card-menu-position.js";
import { sortBoardsForColumn } from "./dashboard-week.js";
import { KindBadge } from "./KindBadge.js";
import { orderedSlots, releaseSpan, shortsCardChip, shortsStripLabel } from "./shorts-card.js";
import type { StoryboardGateAction } from "./StoryboardGateDialog.js";

const DRAG_MIME = "application/x-kola-board";

export interface StatusBoardProps {
  boards: BoardSummary[];
  onOpen: (board: BoardSummary) => void;
  /** Requested stage change (same-stage drops are filtered out here). */
  onMove: (board: BoardSummary, toStatus: BoardStatus) => void;
  /**
   * Requested storyboard sign-off. Never from a drag — there is no column for
   * "approved" — only from the card menu, and only when `canApprove`.
   */
  onStoryboardGate: (board: BoardSummary, action: StoryboardGateAction) => void;
  /** Archive from the card menu — the dashboard owns the confirm + the refetch. */
  onArchive: (board: BoardSummary) => void;
  /** F-137: the script deadline moved on a card. `YYYY-MM-DD`. */
  onSetScriptDue: (board: BoardSummary, dateKey: string) => void;
  /** F-141: a Shorts week's shoot day or edit deadline moved on its card. */
  onSetPlanDate: (board: BoardSummary, field: "shootDate" | "editDueDate", dateKey: string) => void;
  /** F-141: "Plan next week" from a Shorts card — the next batch, one week on. */
  onPlanNextWeek: (board: BoardSummary) => void;
  /** This user holds `review.approve`: the sign-off entries appear on cards. */
  canApprove: boolean;
}

export function StatusBoard({
  boards,
  onOpen,
  onMove,
  onStoryboardGate,
  onArchive,
  onSetScriptDue,
  onSetPlanDate,
  onPlanNextWeek,
  canApprove,
}: StatusBoardProps) {
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const today = todayDateKey();

  const dropped = () => {
    setDragOver(null);
    setDraggingId(null);
  };

  const stageColumn = (status: BoardStatus) => {
    // F-141: soonest date first — the board waiting on this week's day sits
    // above the one waiting on next month's, whatever their ages.
    const cards = sortBoardsForColumn(
      boards.filter((board) => board.status === status),
      today,
    );
    return (
      <Column
        key={status}
        id={status}
        mime={DRAG_MIME}
        label={BOARD_STATUS_LABELS[status]}
        count={cards.length}
        dragOver={dragOver === status}
        onDragOver={() => setDragOver(status)}
        onDragLeave={() => setDragOver((prev) => (prev === status ? null : prev))}
        onDrop={(boardId) => {
          dropped();
          const board = boards.find((b) => b.id === boardId);
          if (board && board.status !== status) onMove(board, status);
        }}
      >
        {cards.map((board) => (
          <BoardCard
            key={board.id}
            board={board}
            dragging={draggingId === board.id}
            canApprove={canApprove}
            onOpen={() => onOpen(board)}
            onMove={(toStatus) => onMove(board, toStatus)}
            onStoryboardGate={(action) => onStoryboardGate(board, action)}
            onArchive={() => onArchive(board)}
            onSetScriptDue={(dateKey) => onSetScriptDue(board, dateKey)}
            onSetPlanDate={(field, dateKey) => onSetPlanDate(board, field, dateKey)}
            onPlanNextWeek={() => onPlanNextWeek(board)}
            onDragState={(active) => setDraggingId(active ? board.id : null)}
          />
        ))}
      </Column>
    );
  };

  return <div className="status-board">{BOARD_STATUSES.map(stageColumn)}</div>;
}

/**
 * One kanban column — a drop target for the board drag payload. `mime` stays
 * a prop (rather than the constant inlined) because it is what makes the
 * column refuse a drag that is not a board's: a file dropped on the dashboard
 * must do nothing at all.
 */
function Column({
  id,
  mime,
  label,
  count,
  dragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  children,
}: {
  id: string;
  mime: string;
  label: string;
  count: number;
  dragOver: boolean;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: (boardId: string) => void;
  children: ReactNode;
}) {
  return (
    <div
      className={`status-column status-${id}${dragOver ? " drag-over" : ""}`}
      onDragOver={(event: DragEvent) => {
        if (!event.dataTransfer.types.includes(mime)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        onDragOver();
      }}
      onDragLeave={(event: DragEvent) => {
        // Ignore leave events fired when entering a child of the column.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        onDragLeave();
      }}
      onDrop={(event: DragEvent) => {
        if (!event.dataTransfer.types.includes(mime)) return;
        event.preventDefault();
        onDrop(event.dataTransfer.getData(mime));
      }}
    >
      <div className="status-column-header">
        <span className="stage-dot" aria-hidden />
        <span className="status-column-title">{label}</span>
        <span className="status-column-count">{count}</span>
      </div>
      <div className="status-column-cards">
        {children}
        {count === 0 && <div className="status-column-empty">No boards</div>}
      </div>
    </div>
  );
}

/** One board, in its stage's column. */
function BoardCard({
  board,
  dragging,
  canApprove,
  onOpen,
  onMove,
  onStoryboardGate,
  onArchive,
  onSetScriptDue,
  onSetPlanDate,
  onPlanNextWeek,
  onDragState,
}: {
  board: BoardSummary;
  dragging: boolean;
  canApprove: boolean;
  onOpen: () => void;
  onMove: (toStatus: BoardStatus) => void;
  onStoryboardGate: (action: StoryboardGateAction) => void;
  onArchive: () => void;
  onSetScriptDue: (dateKey: string) => void;
  onSetPlanDate: (field: "shootDate" | "editDueDate", dateKey: string) => void;
  onPlanNextWeek: () => void;
  onDragState: (active: boolean) => void;
}) {
  // The sign-off, first and only on a board that is actually waiting for it:
  // a project manager scanning Request Review should not have to read past
  // five "Move to …" rows to find the two entries that matter.
  const awaitingSignOff = canApprove && board.status === "request_review";
  const menu: CardMenuEntry[] = [
    ...(awaitingSignOff
      ? ([
          {
            key: "approve",
            label: "Approve storyboard…",
            onPick: () => onStoryboardGate("approve"),
          },
          {
            key: "send-back",
            label: "Send storyboard back…",
            onPick: () => onStoryboardGate("send_back"),
          },
          "separator",
        ] satisfies CardMenuEntry[])
      : []),
    ...BOARD_STATUSES.filter((status) => status !== board.status).map((status) => ({
      key: status,
      label: `Move to ${BOARD_STATUS_LABELS[status]}`,
      onPick: () => onMove(status),
    })),
    "separator",
    // F-141: the next batch is this form with the anchor moved a week on —
    // the one case that actually recurs, so it is one click from the card.
    ...(board.kind === "shorts"
      ? ([{ key: "plan-next", label: "Plan next week…", onPick: onPlanNextWeek }, "separator"] satisfies CardMenuEntry[])
      : []),
    { key: "archive", label: "Archive board", onPick: onArchive, danger: true },
  ];

  return (
    <div
      className={`board-card board-card-${board.kind === "shorts" ? "short" : "long"}${dragging ? " dragging" : ""}`}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData(DRAG_MIME, board.id);
        event.dataTransfer.effectAllowed = "move";
        onDragState(true);
      }}
      onDragEnd={() => onDragState(false)}
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter") onOpen();
      }}
    >
      {/* F-148: every card says its kind first — a Short and a long-form
          video are different jobs, and the column is the stage, not the kind. */}
      <div className="board-card-kind">
        <KindBadge kind={board.kind} slots={board.shorts.length} />
      </div>
      <div className="board-card-title">{board.name}</div>
      {/* F-135: when the script is due — on the one column where that is the
          question. Past the date and still here, the chip turns red: the
          card is the reminder, so the reminder has to be readable across a
          column without opening anything.

          F-137: the chip IS the control that moves the date. A deadline is
          renegotiated far more often than a board is renamed, and the place
          people go to change it is the place it is written — not a menu, and
          not a settings screen. `stopPropagation` because the card itself
          opens the board on click; the picker must not also navigate away. */}
      {board.status === "scripting" && board.scriptFinishDate !== null && (
        <div
          className="board-card-gates"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          // The card is draggable, and a press on the chip is a click, not the
          // start of a drag — without this the picker never opens on a slow
          // press because the drag steals the pointer.
          draggable
          onDragStart={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <DateButton
            value={board.scriptFinishDate}
            onChange={onSetScriptDue}
            placeholder="Set script due date"
            prefix={board.kind === "shorts" && board.shorts.length > 1 ? "Scripts due" : "Script due"}
            context={board.name}
            className={`date-button-chip${isPastDateKey(board.scriptFinishDate) ? " date-button-chip-overdue" : ""}`}
          />
        </div>
      )}
      {/* F-140: a Shorts week says what day it is for the batch and how far
          along the five are — the two things a reader wants without opening
          it (docs/11 §4.3). */}
      {board.kind === "shorts" && <ShortsCardBody board={board} onSetPlanDate={onSetPlanDate} />}
      {/* F-133: the music, from the shoot onwards — the one thing about a
          board in these columns that its stage cannot say. Before the shoot
          there is usually nothing to score, so the tag would only add noise. */}
      {showsBgmStems(board) && (
        <div className="board-card-gates">
          <BgmStemsTag stems={board.bgmStems} />
        </div>
      )}
      {/* A signed-off storyboard normally leaves this column the same second,
          so this chip is a rarity: it means the approval landed but the
          advance's mirrors did not, and the board needs a human. */}
      {board.status === "request_review" && board.storyboardApprovedAt !== null && (
        <div className="board-card-gates">
          <span className="bgm-chip bgm-chip-approved">Storyboard · Approved</span>
        </div>
      )}
      <div className="board-card-meta">
        <span>{formatCardDate(board.createdAt)}</span>
        <CardMenu label={`Actions for ${board.name}`} tip="Board actions" entries={menu} />
      </div>
    </div>
  );
}

/**
 * The Shorts-only part of a card: the kind badge with the date that is the
 * question in this column (red once it has passed with the step not done —
 * the same rule as the Script-due chip, which this card keeps while
 * Scripting), the strip of slot states (one dot since F-143; five on an
 * older week), and the release day (a span on a week of five).
 * All of it derives from the board record; nothing here is editable yet.
 */
function ShortsCardBody({
  board,
  onSetPlanDate,
}: {
  board: BoardSummary;
  onSetPlanDate: (field: "shootDate" | "editDueDate", dateKey: string) => void;
}) {
  const chip = board.status === "scripting" ? null : shortsCardChip(board, todayDateKey());
  const slots = orderedSlots(board);
  const span = releaseSpan(board);
  const label = shortsStripLabel(board);
  // F-141: the shoot day and the edit deadline move from the card, the way
  // the script date does (same DateButton chip, same drag-guard). A release
  // day moves from the board's rail, where the short it belongs to is.
  const editableField =
    chip === null ? null : chip.label === "Shoot" ? "shootDate" : chip.label === "Edit due" ? "editDueDate" : null;
  return (
    <>
      {chip !== null && (
      <div
        className="board-card-gates"
        onClick={(event) => {
          if (editableField !== null) event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (editableField !== null) event.stopPropagation();
        }}
        draggable={editableField !== null}
        onDragStart={(event) => {
          if (editableField === null) return;
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        {editableField !== null && (
          <DateButton
            value={chip.dateKey}
            onChange={(dateKey) => onSetPlanDate(editableField, dateKey)}
            placeholder={`Set ${chip.label.toLowerCase()}`}
            prefix={chip.label}
            context={board.name}
            className={`date-button-chip${chip.overdue ? " date-button-chip-overdue" : ""}`}
          />
        )}
        {editableField === null && (
          <span className={`shorts-chip${chip.overdue ? " shorts-chip-overdue" : ""}`}>
            {chip.label} {formatDateKeyWithWeekday(chip.dateKey)}
          </span>
        )}
      </div>
      )}
      {slots.length > 0 && (
        <div className="shorts-strip" role="img" aria-label={label}>
          <span className="shorts-dots">
            {slots.map((slot) => (
              <span
                key={slot.slot}
                className={`shorts-dot shorts-dot-${slot.state}`}
                title={
                  slots.length > 1
                    ? `Short ${slot.slot} · ${slot.state} · ${formatDateKeyWithWeekday(slot.releaseDate)}`
                    : `${slot.state} · releases ${formatDateKeyWithWeekday(slot.releaseDate)}`
                }
              />
            ))}
          </span>
          <span className="shorts-strip-label">{label}</span>
        </div>
      )}
      {span !== null && (
        <div className="board-card-releases">
          {span.first === span.last
            ? `Releases ${formatDateKey(span.first)}`
            : `Releases ${formatDateKey(span.first)} → ${formatDateKey(span.last)}`}
        </div>
      )}
    </>
  );
}

type CardMenuEntry =
  | { key: string; label: string; onPick: () => void; danger?: boolean }
  | "separator";

/**
 * The "⋯" button and its popover. Owns its open state: it closes on Escape,
 * on a press anywhere outside (a full-viewport backdrop, the same dismissal
 * the board's context menu uses), and when an entry is picked — a menu left
 * open behind the dialog it just opened reads as a second, stuck, menu.
 *
 * THE MENU IS NOT A CHILD OF THE CARD. Every kanban column scrolls its own
 * cards, and a scroll container clips its descendants whatever their z-index:
 * rendered in place, the menu was cut off at the column's edge and its lower
 * rows were unreachable. It renders in the body-level portal layer instead
 * (`useKolaPortalTarget` — outside `.kola-root` it would lose every colour
 * token) as `position: fixed`, so it floats OVER the board, and
 * `placeCardMenu` puts it under the button, flipping above near the window
 * floor. Being outside the card also means a press that dismisses the menu no
 * longer falls through to the card and opens the board.
 *
 * `label` is the accessible name (it names the board, for a screen reader
 * walking the column); `tip` is the short hover label — it has to fit INSIDE
 * the card, or the bubble widens the column's scroll area.
 */
function CardMenu({
  label,
  tip,
  entries,
}: {
  label: string;
  tip: string;
  entries: CardMenuEntry[];
}) {
  const portalTarget = useKolaPortalTarget();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  // Null until the menu has been rendered once and measured — it is hidden
  // for that pass, so a menu never flashes at the viewport's top-left.
  const [at, setAt] = useState<CardMenuPosition | null>(null);

  // Layout effect: measure and place BEFORE paint, so the reveal is one frame.
  useLayoutEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (button === null || menu === null) return;
    const rect = button.getBoundingClientRect();
    setAt(
      placeCardMenu(
        { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        { width: menu.offsetWidth, height: menu.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [open, entries.length]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <div className="board-card-menu-wrap">
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-ghost board-card-menu-btn"
        aria-label={label}
        data-tip={tip}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        ⋯
      </button>
      {open &&
        createPortal(
          <>
            {/* pointerdown, NOT mousedown: a touch long-press leaves the finger
                down, and the lift fires compatibility MOUSE events that would
                close the menu the same instant it opened. */}
            <div
              className="board-card-menu-backdrop"
              onPointerDown={(event) => {
                event.preventDefault();
                setOpen(false);
              }}
              onContextMenu={(event) => {
                event.preventDefault();
                setOpen(false);
              }}
            />
            <div
              ref={menuRef}
              className="board-card-menu"
              role="menu"
              style={
                at === null
                  ? { left: 0, top: 0, visibility: "hidden" }
                  : { left: at.left, top: at.top }
              }
            >
              {entries.map((entry, index) =>
                entry === "separator" ? (
                  <div key={`separator-${index}`} className="board-card-menu-separator" />
                ) : (
                  <button
                    key={entry.key}
                    type="button"
                    role="menuitem"
                    className={`board-card-menu-item${entry.danger ? " board-card-menu-item-danger" : ""}`}
                    // pointerdown, not click: it matches the backdrop, so the
                    // two can never race over which of them saw the press.
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setOpen(false);
                      entry.onPick();
                    }}
                  >
                    {entry.label}
                  </button>
                ),
              )}
            </div>
          </>,
          portalTarget,
        )}
    </div>
  );
}

function formatCardDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
